import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Leaf, MapPin, Phone } from "lucide-react";

const NAV = [
  { to: "/", key: "nav.home" },
  { to: "/teknoloji", key: "nav.technology" },
  { to: "/donanim", key: "nav.hardware" },
  { to: "/cozumler", key: "nav.solutions" },
  { to: "/neden-biz", key: "nav.whyus" },
  { to: "/iletisim", key: "nav.contact" },
];

export const Footer = () => {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const navigate = useNavigate();
  const goAdmin = () => navigate("/admin");

  return (
    <footer className="bg-[#1a3636] text-[#f8faf9]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          <div className="md:col-span-4">
            <div className="flex items-center gap-2 mb-5">
              <Leaf size={22} strokeWidth={1.5} className="text-[#2b8a7e]" />
              <span className="font-display font-extrabold tracking-tight text-xl">
                PERGAMON BIO SYSTEMS
              </span>
            </div>
            <p className="text-[#f8faf9]/60 max-w-md leading-relaxed">
              {t("footer.tagline")}
            </p>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-widest text-[#2b8a7e] mb-4">
              {t("footer.nav")}
            </p>
            <ul className="space-y-3">
              {NAV.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    data-testid={`footer-link-${item.to === "/" ? "home" : item.to.slice(1)}`}
                    className="text-[#f8faf9]/70 hover:text-[#2b8a7e] transition-colors duration-300"
                  >
                    {t(item.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-5">
            <p className="text-xs uppercase tracking-widest text-[#2b8a7e] mb-4">
              {t("footer.contact")}
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-[#f8faf9]/80" data-testid="footer-address">
                <MapPin size={16} strokeWidth={1.5} className="text-[#2b8a7e] mt-0.5 shrink-0" />
                <span className="leading-relaxed">{t("contact.address")}</span>
              </li>
              <li>
                <a
                  href={`tel:${t("contact.phone").replace(/\s+/g, "")}`}
                  data-testid="footer-phone"
                  className="inline-flex items-center gap-2 text-[#f8faf9]/80 hover:text-[#2b8a7e] transition-colors duration-300"
                >
                  <Phone size={16} strokeWidth={1.5} className="text-[#2b8a7e]" />
                  {t("contact.phone")}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${t("contact.emailGeneral")}`}
                  data-testid="footer-email"
                  className="inline-flex items-center gap-2 text-[#f8faf9]/80 hover:text-[#2b8a7e] transition-colors duration-300"
                >
                  <Mail size={16} strokeWidth={1.5} className="text-[#2b8a7e]" />
                  {t("contact.emailGeneral")}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${t("contact.emailSales")}`}
                  data-testid="footer-email-sales"
                  className="inline-flex items-center gap-2 text-[#f8faf9]/80 hover:text-[#2b8a7e] transition-colors duration-300"
                >
                  <Mail size={16} strokeWidth={1.5} className="text-[#2b8a7e]" />
                  {t("contact.emailSales")}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-[#f8faf9]/50">
            ©{" "}
            {/* Hidden admin trigger — invisible to visitors */}
            <span
              onClick={goAdmin}
              onKeyDown={(e) => { if (e.key === "Enter") goAdmin(); }}
              role="presentation"
              aria-hidden="true"
              tabIndex={-1}
              data-testid="footer-hidden-trigger"
              className="inline-block align-baseline"
              style={{ cursor: "text", userSelect: "none" }}
              title=""
            >
              {year}
            </span>{" "}
            Pergamon Bio Systems. {t("footer.rights")}
          </p>
          <p className="text-xs uppercase tracking-widest text-[#f8faf9]/40">
            Wate Logic · Hydroflow
          </p>
        </div>
      </div>
    </footer>
  );
};
